// mbscan manager-client の同名部品と共通。片側だけで変えない
import { useNavigate } from '@tanstack/react-router'
import type { LucideIcon } from 'lucide-react'
import { LogIn, RotateCw } from 'lucide-react'

import type { ErrorPanelLayout } from '@/components/ui/error-panel'
import { ErrorPanel } from '@/components/ui/error-panel'
import type { GraphqlErrorClassification } from '@/lib/apollo/error-classification'
import { classifyGraphqlError } from '@/lib/apollo/error-classification'
import { errorPanelCopy } from '@/lib/error-panel-copy'

// GraphQL の取得エラーを分類し、lib/error-panel-copy.ts の既定文言で ErrorPanel を出す。
// 画面ごとに分類の分岐と文言を手書きしないための入口。分類以外の理由（ロール不足・URL 不正等）で
// 画面を出せないときは ErrorPanel を直接使う
export function GraphqlErrorPanel({
  error,
  classification,
  subject,
  onRetry,
  description,
  action: actionOverride,
  layout,
}: {
  /** 分類済みの値を持っている呼び出し側は classification を渡す（両方渡したら classification を優先） */
  error?: unknown
  classification?: GraphqlErrorClassification
  /** 取得しようとしたもの。「ミルシート一覧を取得できませんでした」のようにタイトルへ埋め込む */
  subject: string
  /** 渡すと「再試行」ボタンを出す（認証切れでは出さず再ログインを促す） */
  onRetry?: () => void
  /**
   * 画面固有の事情（運営メンバー限定等）を伝えたいときだけ既定の説明文を上書きする。
   * 認証切れは画面に関係なく同じ案内にするため上書きしない
   */
  description?: string
  /**
   * 再試行ではなく画面固有の遷移（「テナント選択に戻る」等）を出したいときに使う。
   * description と同じく、認証切れの再ログインボタンは上書きしない
   */
  action?: { label: string; onClick: () => void; icon: LucideIcon }
  layout?: ErrorPanelLayout
}) {
  const navigate = useNavigate()
  const copy = errorPanelCopy(classification ?? classifyGraphqlError(error), {
    subject,
    canRetry: onRetry !== undefined,
  })
  const action = (() => {
    if (copy.action === null) {
      return undefined
    }
    if (copy.action.kind === 'sign-out') {
      return {
        label: copy.action.label,
        icon: LogIn,
        onClick: () => {
          void navigate({ to: '/sign_out' })
        },
      }
    }
    return onRetry === undefined ? undefined : { label: copy.action.label, icon: RotateCw, onClick: onRetry }
  })()
  const isSignOut = copy.action?.kind === 'sign-out'

  return (
    <ErrorPanel
      title={copy.title}
      description={isSignOut ? copy.description : (description ?? copy.description)}
      action={isSignOut ? action : (actionOverride ?? action)}
      layout={layout}
    />
  )
}
